import React, {
  useState,
  useEffect,
  useLayoutEffect,
  useCallback,
  useRef,
  useMemo,
} from "react";
import Sidemenu from "./sidemenu/Sidemenu";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import Header from "./header";
import { Image } from "../../core";
import { FiMenu } from "react-icons/fi";
import FloatingLinesBackground from "../../backgrounds/FloatingLines";
import ReleaseNotesPopup from "../../shared/ReleaseNotesPopup";
type Props = {
  children?: React.ReactNode;
  location: string;
  titlePage?: string;
  nofooter?: boolean;
};

const SIDEMENU_COLLAPSED_KEY = "sidemenu_collapsed";
type NavShape = {
  width: number;
  height: number;
  path: string;
};
type NavMetrics = {
  width: number;
  height: number;
  anchorX: number;
  notchWidth: number;
  depth: number;
  useCurve: boolean;
};
type GradientStop = {
  offset: string;
  color: string;
  opacity: number;
};
type ShadowLayer = {
  dx: number;
  dy: number;
  stdDeviation: number;
  color: string;
  opacity: number;
};

const clampValue = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);
const lerp = (from: number, to: number, t: number) => from + (to - from) * t;
const easeOutCubic = (t: number) => 1 - Math.pow(1 - t, 3);
const easeOutBack = (t: number, tension = 1.25) => {
  const clamped = clampValue(t, 0, 1);
  const overshoot = tension;
  const normalized = clamped - 1;
  return (
    1 +
    (overshoot + 1) * Math.pow(normalized, 3) +
    overshoot * Math.pow(normalized, 2)
  );
};
const hexToRgb = (hex: string) => {
  const normalized = hex.replace("#", "");
  const expanded =
    normalized.length === 3
      ? normalized
          .split("")
          .map((char) => char + char)
          .join("")
      : normalized;
  const int = parseInt(expanded, 16);
  return {
    r: (int >> 16) & 255,
    g: (int >> 8) & 255,
    b: int & 255,
  };
};
const rgbToHex = ({ r, g, b }: { r: number; g: number; b: number }) =>
  `#${[r, g, b]
    .map((value) => clampValue(Math.round(value), 0, 255))
    .map((value) => value.toString(16).padStart(2, "0"))
    .join("")}`;
const interpolateHex = (from: string, to: string, t: number) => {
  const start = hexToRgb(from);
  const end = hexToRgb(to);
  return rgbToHex({
    r: lerp(start.r, end.r, t),
    g: lerp(start.g, end.g, t),
    b: lerp(start.b, end.b, t),
  });
};
const buildPath = (metrics: NavMetrics) => {
  const { width, height, anchorX, notchWidth, depth, useCurve } = metrics;
  if (!useCurve || notchWidth <= 0.2 || depth <= 0.2) {
    return `M0 0 H ${width} V ${height} H 0 Z`;
  }

  const halfNotch = notchWidth / 2;
  const startX = clampValue(anchorX - halfNotch, 0, width);
  const endX = clampValue(anchorX + halfNotch, 0, width);
  const leftControlX = startX + (anchorX - startX) * 0.45;
  const rightControlX = endX - (endX - anchorX) * 0.45;
  const adjustedDepth = clampValue(depth, 6, height - 2);

  return [
    `M 0 0`,
    `H ${startX}`,
    `C ${leftControlX} 0 ${
      anchorX - halfNotch * 0.35
    } ${adjustedDepth} ${anchorX} ${adjustedDepth}`,
    `C ${
      anchorX + halfNotch * 0.35
    } ${adjustedDepth} ${rightControlX} 0 ${endX} 0`,
    `H ${width}`,
    `V ${height}`,
    `H 0`,
    `Z`,
  ].join(" ");
};

const DEFAULT_METRICS: NavMetrics = {
  width: 320,
  height: 70,
  anchorX: 160,
  notchWidth: 0,
  depth: 0,
  useCurve: false,
};

const MOBILE_NAV_GRADIENT_ID = "mobile-nav-curve-gradient";
const MOBILE_NAV_SHADOW_ID = "mobile-nav-curve-shadow";

export default function Layout({ nofooter, titlePage, location }: Props) {
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem(SIDEMENU_COLLAPSED_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();
  const locationInfo = useLocation();
  const isSupportActive = locationInfo.pathname.startsWith("/support");
  const navRef = useRef<HTMLDivElement | null>(null);
  const menuButtonRef = useRef<HTMLButtonElement | null>(null);
  const supportButtonRef = useRef<HTMLButtonElement | null>(null);
  const [navShape, setNavShape] = useState<NavShape>(() => ({
    width: DEFAULT_METRICS.width,
    height: DEFAULT_METRICS.height,
    path: buildPath(DEFAULT_METRICS),
  }));
  const metricsRef = useRef<NavMetrics>(DEFAULT_METRICS);
  const animationFrameRef = useRef<number | null>(null);
  const gradientAnimationFrameRef = useRef<number | null>(null);
  const gradientProgressRef = useRef(0);
  const [gradientProgress, setGradientProgress] = useState(0);
  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
  }, []);
  const cancelGradientAnimation = useCallback(() => {
    if (gradientAnimationFrameRef.current !== null) {
      cancelAnimationFrame(gradientAnimationFrameRef.current);
      gradientAnimationFrameRef.current = null;
    }
  }, []);

  const applyMetrics = useCallback(
    (target: NavMetrics, animate: boolean) => {
      const from = metricsRef.current;
      const enteringCurve = target.useCurve && !from.useCurve;
      const initial: NavMetrics = {
        ...from,
        width: target.width,
        height: target.height,
        useCurve: from.useCurve || target.useCurve,
      };

      const shouldAnimate =
        animate &&
        (enteringCurve ||
          Math.abs(initial.anchorX - target.anchorX) > 0.5 ||
          Math.abs(initial.notchWidth - target.notchWidth) > 0.5 ||
          Math.abs(initial.depth - target.depth) > 0.5 ||
          initial.useCurve !== target.useCurve);

      if (!shouldAnimate) {
        cancelAnimation();
        metricsRef.current = target;
        setNavShape({
          width: target.width,
          height: target.height,
          path: buildPath(target),
        });
        return;
      }

      cancelAnimation();
      const duration = enteringCurve ? 480 : 360;
      const start = performance.now();

      const tick = (timestamp: number) => {
        const elapsed = timestamp - start;
        const progress = clampValue(elapsed / duration, 0, 1);
        const eased = easeOutCubic(progress);
        const curveProgress = enteringCurve
          ? easeOutBack(progress, 1.35)
          : eased;
        const depthProgress = enteringCurve
          ? easeOutBack(progress, 1.15)
          : eased;

        const notchWidth = lerp(
          initial.notchWidth,
          target.notchWidth,
          curveProgress
        );
        const depth = lerp(initial.depth, target.depth, depthProgress);
        const anchorX = lerp(initial.anchorX, target.anchorX, eased);
        const useCurve = enteringCurve
          ? progress > 0.05
          : (initial.useCurve || target.useCurve) &&
            (notchWidth > 0.15 || depth > 0.15);

        const current: NavMetrics = {
          width: target.width,
          height: target.height,
          anchorX,
          notchWidth,
          depth,
          useCurve,
        };

        metricsRef.current = current;
        setNavShape({
          width: current.width,
          height: current.height,
          path: buildPath(current),
        });

        if (progress < 1) {
          animationFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        metricsRef.current = target;
        setNavShape({
          width: target.width,
          height: target.height,
          path: buildPath(target),
        });
        animationFrameRef.current = null;
      };

      animationFrameRef.current = requestAnimationFrame(tick);
    },
    [cancelAnimation]
  );

  useEffect(() => {
    localStorage.setItem(SIDEMENU_COLLAPSED_KEY, JSON.stringify(isCollapsed));
  }, [isCollapsed]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const updateIsMobile = () => {
      setIsMobile(window.innerWidth <= 640);
    };

    updateIsMobile();
    window.addEventListener("resize", updateIsMobile);

    return () => {
      window.removeEventListener("resize", updateIsMobile);
    };
  }, []);

  useEffect(() => {
    if (!isMobile) {
      setIsMobileMenuOpen(false);
    }
  }, [isMobile]);

  useEffect(() => {
    return () => cancelAnimation();
  }, [cancelAnimation]);
  useEffect(() => {
    return () => cancelGradientAnimation();
  }, [cancelGradientAnimation]);

  const onClose = () => {
    if (isMobile) {
      setIsMobileMenuOpen(false);
    }
  };

  const toggleMobileSidebar = () => {
    setIsMobileMenuOpen((prev) => !prev);
  };

  const handleTicketClick = () => {
    setIsMobileMenuOpen(false);
    navigate("/support");
  };

  const viewBoxWidth = Math.max(navShape.width, 1);
  const viewBoxHeight = Math.max(navShape.height, 1);
  const navPath =
    navShape.path || `M0 0 H ${viewBoxWidth} V ${viewBoxHeight} H 0 Z`;
  const animateGradientTo = useCallback(
    (target: number) => {
      const clamped = clampValue(target, 0, 1);
      const startValue = gradientProgressRef.current;
      if (Math.abs(startValue - clamped) < 0.001) {
        cancelGradientAnimation();
        gradientProgressRef.current = clamped;
        setGradientProgress(clamped);
        return;
      }

      cancelGradientAnimation();
      const duration = 420;
      const start = performance.now();

      const tick = (timestamp: number) => {
        const elapsed = timestamp - start;
        const progress = clampValue(elapsed / duration, 0, 1);
        const eased = easeOutCubic(progress);
        const current = lerp(startValue, clamped, eased);

        gradientProgressRef.current = current;
        setGradientProgress(current);

        if (progress < 1) {
          gradientAnimationFrameRef.current = requestAnimationFrame(tick);
          return;
        }

        gradientAnimationFrameRef.current = null;
      };

      gradientAnimationFrameRef.current = requestAnimationFrame(tick);
    },
    [cancelGradientAnimation]
  );
  useEffect(() => {
    const target = isSupportActive ? 1 : isMobileMenuOpen ? 0.45 : 0;
    animateGradientTo(target);
  }, [animateGradientTo, isMobileMenuOpen, isSupportActive]);
  const gradientStops = useMemo<GradientStop[]>(() => {
    const baseStops: GradientStop[] = [
      { offset: "0%", color: "#8B5CF6", opacity: 0.96 },
      { offset: "55%", color: "#A855F7", opacity: 0.94 },
      { offset: "100%", color: "#C084FC", opacity: 0.92 },
    ];
    const targetStops: GradientStop[] = [
      { offset: "0%", color: "#4338CA", opacity: 0.94 },
      { offset: "55%", color: "#3B82F6", opacity: 0.92 },
      { offset: "100%", color: "#93C5FD", opacity: 0.9 },
    ];

    return baseStops.map((baseStop, index) => {
      const targetStop =
        targetStops[index] ?? targetStops[targetStops.length - 1];
      return {
        offset: baseStop.offset,
        color: interpolateHex(
          baseStop.color,
          targetStop.color,
          gradientProgress
        ),
        opacity: lerp(baseStop.opacity, targetStop.opacity, gradientProgress),
      };
    });
  }, [gradientProgress]);
  const shadowLayers = useMemo<ShadowLayer[]>(() => {
    const baseLayers: ShadowLayer[] = [
      { dx: 0, dy: 24, stdDeviation: 18, color: "#9F87FF", opacity: 0.24 },
      { dx: 0, dy: 8, stdDeviation: 12, color: "#FBCFE8", opacity: 0.2 },
    ];
    const targetLayers: ShadowLayer[] = [
      { dx: 0, dy: 20, stdDeviation: 17, color: "#60A5FA", opacity: 0.22 },
      { dx: 0, dy: 9, stdDeviation: 12, color: "#BFDBFE", opacity: 0.18 },
    ];

    return baseLayers.map((baseLayer, index) => {
      const targetLayer =
        targetLayers[index] ?? targetLayers[targetLayers.length - 1];
      return {
        dx: lerp(baseLayer.dx, targetLayer.dx, gradientProgress),
        dy: lerp(baseLayer.dy, targetLayer.dy, gradientProgress),
        stdDeviation: lerp(
          baseLayer.stdDeviation,
          targetLayer.stdDeviation,
          gradientProgress
        ),
        color: interpolateHex(
          baseLayer.color,
          targetLayer.color,
          gradientProgress
        ),
        opacity: lerp(baseLayer.opacity, targetLayer.opacity, gradientProgress),
      };
    });
  }, [gradientProgress]);
  const gradientStrokeColor = useMemo(() => {
    const opacity = lerp(0.24, 0.14, gradientProgress);
    return `rgba(255,255,255,${opacity})`;
  }, [gradientProgress]);
  // Shared styles for the curved mobile navigation buttons.
  const mobileButtonBaseClass =
    "group relative w-[56px] h-[56px] flex flex-col items-center justify-center gap-0.5 rounded-2xl border border-white/20 bg-white/10 text-white shadow-lg backdrop-blur-sm transition-all duration-300 ease-out transform hover:-translate-y-1 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-white/90";
  const mobileButtonActiveClass = "bg-white border-0 text-gray-900 shadow-2xl";
  const mobileButtonInactiveClass = "hover:bg-white/20";

  const updateNavShape = useCallback(() => {
    if (!isMobile) {
      return;
    }

    const navElement = navRef.current;
    if (!navElement) {
      return;
    }

    const navRect = navElement.getBoundingClientRect();
    const width = navRect.width || 1;
    const height = navRect.height || 1;
    const supportElement = supportButtonRef.current;

    const targetMetrics: NavMetrics = (() => {
      if (isSupportActive && supportElement) {
        const iconElement = supportElement.querySelector("img, svg");
        const anchorRect = iconElement
          ? iconElement.getBoundingClientRect()
          : supportElement.getBoundingClientRect();
        const anchorX = anchorRect.left + anchorRect.width / 2 - navRect.left;
        const anchorBottom = anchorRect.bottom - navRect.top;
        const notchWidth = Math.max(anchorRect.width * 2, width * 0.22);
        const depth = clampValue(anchorBottom + 6, 28, height - 4);

        return {
          width,
          height,
          anchorX,
          notchWidth,
          depth,
          useCurve: true,
        };
      }

      return {
        width,
        height,
        anchorX: width / 2,
        notchWidth: 0,
        depth: 0,
        useCurve: false,
      };
    })();

    const previous = metricsRef.current;
    const shouldAnimate =
      isMobile &&
      (targetMetrics.useCurve ||
        previous.useCurve ||
        Math.abs(previous.anchorX - targetMetrics.anchorX) > 0.5 ||
        Math.abs(previous.notchWidth - targetMetrics.notchWidth) > 0.5 ||
        Math.abs(previous.depth - targetMetrics.depth) > 0.5);

    applyMetrics(targetMetrics, shouldAnimate);
  }, [applyMetrics, isMobile, isSupportActive]);

  useLayoutEffect(() => {
    updateNavShape();
  }, [updateNavShape, isMobileMenuOpen, locationInfo.pathname]);

  useEffect(() => {
    if (!isMobile) {
      return;
    }

    updateNavShape();
    const handleResize = () => updateNavShape();

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [isMobile, updateNavShape]);

  return (
    <div className="flex w-full h-screen flex-grow overflow-hidden relative bg-gradient-to-tl from-primary-75 to-pink-50">
      {/* if user was is assistant page show floating lines background */}
      {/* {locationInfo.pathname === "/assistant" && (
        <div className="absolute inset-0 z-[5]">
        <FloatingLinesBackground
        enabledWaves={["top", "middle", "bottom"]}
        lineCount={[10, 15, 20]}
        lineDistance={[8, 6, 4]}
        bendRadius={5.0}
        bendStrength={-0.5}
          interactive={true}
          parallax={true}
        />
        </div>
      )} */}
      <div
        className="flex w-full flex-col ~mx-2/5 overflow-y-auto justify-start items-start transition-all duration-300 ease-in-out"
        style={{
          paddingRight: isMobile ? "0px" : isCollapsed ? "130px" : "240px",
          paddingBottom: isMobile ? "80px" : "0px",
        }}
      >
        <Header />
        <Outlet />
        {/* {!nofooter && <Footer location={location} />} */}
      </div>
      {true && (
        <Sidemenu
          onClose={onClose}
          isCollapsed={isCollapsed}
          setIsCollapsed={setIsCollapsed}
          isMobileMenuOpen={isMobileMenuOpen}
          setIsMobileMenuOpen={setIsMobileMenuOpen}
        />
      )}
      <ReleaseNotesPopup />
      {isMobile && (
        <div className="fixed bottom-0 left-0 right-0 z-30 flex justify-center w-full pointer-events-none">
          <div
            ref={navRef}
            className="relative w-full h-[76px] pointer-events-auto"
          >
            <svg
              className="absolute inset-0 h-full w-full"
              viewBox={`0 0 ${viewBoxWidth} ${viewBoxHeight}`}
              preserveAspectRatio="none"
              aria-hidden="true"
            >
              <defs>
                <linearGradient
                  id={MOBILE_NAV_GRADIENT_ID}
                  x1="0%"
                  y1="0%"
                  x2="100%"
                  y2="100%"
                >
                  {gradientStops.map((stop) => (
                    <stop
                      key={stop.offset}
                      offset={stop.offset}
                      stopColor={stop.color}
                      stopOpacity={stop.opacity}
                    />
                  ))}
                </linearGradient>
                <filter
                  id={MOBILE_NAV_SHADOW_ID}
                  x="-35%"
                  y="-120%"
                  width="170%"
                  height="320%"
                >
                  {shadowLayers.map((layer, index) => (
                    <feDropShadow
                      key={`shadow-${index}`}
                      dx={layer.dx}
                      dy={layer.dy}
                      stdDeviation={layer.stdDeviation}
                      floodColor={layer.color}
                      floodOpacity={layer.opacity}
                    />
                  ))}
                </filter>
              </defs>
              <path
                d={navPath}
                fill={`url(#${MOBILE_NAV_GRADIENT_ID})`}
                stroke={gradientStrokeColor}
                strokeWidth="1.4"
                vectorEffect="non-scaling-stroke"
                filter={`url(#${MOBILE_NAV_SHADOW_ID})`}
              />
            </svg>
            <div className="absolute inset-0 flex items-end justify-between px-8 pb-3 gap-6">
              <button
                type="button"
                ref={menuButtonRef}
                onClick={toggleMobileSidebar}
                className={`${mobileButtonBaseClass} ${
                  isMobileMenuOpen
                    ? mobileButtonActiveClass
                    : mobileButtonInactiveClass
                }`}
                aria-label="Toggle navigation menu"
                aria-pressed={isMobileMenuOpen}
              >
                <FiMenu className="text-[20px] transition-transform duration-300 group-hover:scale-110" />
                <span className="text-BodyText/12/Light font-semibold tracking-tight text-current leading-none">
                  منو
                </span>
              </button>
              <button
                type="button"
                ref={supportButtonRef}
                onClick={handleTicketClick}
                className={`${mobileButtonBaseClass} ${
                  isSupportActive
                    ? mobileButtonActiveClass
                    : mobileButtonInactiveClass
                }`}
                aria-label="Open support tickets"
                aria-current={isSupportActive ? "page" : undefined}
              >
                <Image
                  src="/icons/support-white.svg"
                  alt="ticketing"
                  className={`w-6 h-6 transition-transform duration-300 ${
                    isSupportActive
                      ? "scale-110 -translate-y-0.5"
                      : "group-hover:scale-110"
                  }`}
                />
                <span className="text-BodyText/12/Light font-semibold tracking-tight text-current leading-none">
                  پشتیبانی
                </span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
